import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import t from '~t';
import { useDispatch, useSelector } from 'react-redux';
import {
  draftLoad,
  draftCommit,
  draftChange,
  draftCoverUpload,
  oneRemove,
  oneRecover,
} from '~data/actions/bookmarks';
import {
  getDraftItem,
  getDraftStatus,
  makeDraftUnsaved,
} from '~data/selectors/bookmarks';
import { target } from '~target';
import { isFree } from '~data/selectors/user';

import { Error } from '~co/overlay/dialog';
import Form from './form';
import Crash from './crash';
import Gate from './gate';

export default function EditBookmarkContainer(props) {
  const { _id, new: newItem = {} } = props;

  const dispatch = useDispatch();
  const getDraftUnsaved = useMemo(() => makeDraftUnsaved(), []);

  const status = useSelector((state) => getDraftStatus(state, _id));
  const item = useSelector((state) => getDraftItem(state, _id));
  const unsaved = useSelector((state) => getDraftUnsaved(state, _id));
  const free = useSelector((state) => isFree(state));

  //latest values for window listeners / deferred handlers
  const latestRef = useRef();
  latestRef.current = { props, _id, newItem, status, item, unsaved };

  const handlers = useMemo(() => {
    const save = () =>
      new Promise((res, rej) => {
        const { _id } = latestRef.current;

        dispatch(
          draftCommit(_id, res, (e) => {
            Error(e, { id: _id });
            rej(e);
          }),
        );
      });

    return {
      onLoad: () => {
        const { _id, newItem } = latestRef.current;
        dispatch(draftLoad(_id, newItem));
      },

      onChange: (obj) => {
        const { _id } = latestRef.current;
        dispatch(draftChange(_id, obj));
      },

      onCommit: async () => {
        const { status } = latestRef.current;
        if (status != 'new') return save();
      },

      onSave: save,

      onUploadCover: async (file) => {
        //save draft before if it's new
        if (!latestRef.current.item._id) await save();

        await new Promise((res, rej) =>
          dispatch(
            draftCoverUpload(latestRef.current._id, file, res, rej),
          ),
        );

        if (latestRef.current.status != 'new') await save();
      },

      onRemove: () => {
        const {
          item: { _id },
          props,
        } = latestRef.current;
        if (_id)
          dispatch(
            oneRemove(
              _id,
              () => {
                if (props.autoWindowClose) window.close();
              },
              Error,
            ),
          );
      },

      onRecover: () => {
        const {
          item: { _id },
        } = latestRef.current;
        if (_id) dispatch(oneRecover(_id, undefined, Error));
      },
    };
  }, [dispatch]);

  const onWindowClose = useCallback(
    (e) => {
      const { status, unsaved } = latestRef.current;

      //save unsaved changes if user try to close window
      handlers.onCommit();

      if (target == 'web' && unsaved && status != 'new')
        if (e && e.preventDefault) {
          e.preventDefault();
          e.returnValue = '';
        }

      return t.s('unsavedWarning');
    },
    [handlers],
  );

  //mount: load draft + window listeners; unmount: commit + cleanup
  useEffect(() => {
    handlers.onLoad();

    window.addEventListener('beforeunload', onWindowClose);
    //must have in extension
    window.addEventListener('blur', onWindowClose);
    document.addEventListener('mouseleave', onWindowClose);

    return () => {
      handlers.onCommit();

      window.removeEventListener('beforeunload', onWindowClose);
      window.removeEventListener('blur', onWindowClose);
      document.removeEventListener('mouseleave', onWindowClose);
    };
  }, [handlers, onWindowClose]);

  //on _id change: commit previous unsaved draft, load the new one
  const prevRef = useRef({ _id, unsaved, status });
  useEffect(() => {
    const prev = prevRef.current;

    if (_id != prev._id) {
      if (prev.unsaved && prev.status != 'new')
        dispatch(draftCommit(prev._id));

      handlers.onLoad();
    }

    prevRef.current = { _id, unsaved, status };
  });

  //creating is Pro-gated (`new` prop); editing an existing bookmark is
  //not. `item._id` unset means draftLoad hasn't resolved the URL to an
  //existing bookmark yet — the draft is still a create. `isFree` stays
  //false until the profile resolves, so Pro users are never gated during
  //the load window. Gate takes precedence over Crash — a retry can't
  //succeed for a free user anyway.
  const creating = !!newItem.item && !item?._id;
  const Component =
    creating && free ? Gate : status == 'error' ? Crash : Form;

  return (
    <div className='mx-auto flex w-full max-w-[12.5rem]'>
      <Component
        {...props}
        status={status}
        item={item}
        unsaved={unsaved}
        {...handlers}
      />
    </div>
  );
}
